import React, { useState, useEffect, useRef } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import Instruction from '../../components/Instruction'
import BrandEngagement from '../../components/BrandEngagement'
import Permission from '../../components/Permission'
import SplashScreen from '../../components/SplashScreen'
import HowToScan from '../../components/HowToScan'
import { performBandwidthTest } from './BandwidthTest'
import { CAMERA, PHONE } from '../assets'
import { TEXTS, FLOW, LANGUAGE_CONFIG } from '../../constants'
import VideoSDK from '../VyuVideoSdk'
import './VyuVideo.scss'
import posthog from 'posthog-js'
import usePosthogEvent from '../../hooks/use-posthog-event'
import posthogEvents from '../../posthog/eventNames.json'
import { v4 as uuid } from 'uuid';
import config from '../../config';
import { webLogger } from '../../tracing'
import GenuineVideoPopup from '../components/GenuineVideoPopup';
import ReportNow from '../components/ReportNow';
import { getEnvironmentFromDomain } from '../../utils/helperFunctions'
const CACHE_KEY = 'user_geolocation_cache';
const CACHE_TIMESTAMP = 'user_geolocation_timestamp';
const CACHE_DURATION = 5 * 60 * 1000;

// Loader image URL constant
const LOADER_IMAGE_URL = 'https://ennoventure-public-assets.s3.us-east-1.amazonaws.com/static/Enno_Loader.webp';

// Preload function to cache the image
const preloadImage = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      console.log('Image preloaded successfully:', url);
      resolve(img);
    };
    img.onerror = (error) => {
      console.warn('Failed to preload image:', url, error);
      reject(error);
    };
    img.src = url;

    // Also add to document head for additional caching
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = url;
    link.as = 'image';
    link.type = 'image/webp';
    document.head.appendChild(link);
  });
};

const initializePostHog = () => {
  const isProductionDomain = getEnvironmentFromDomain() === 'prod';
  if (window.location.pathname.startsWith('/video/product')) {
    if (isProductionDomain) {
      posthog.init('phc_VHUYtTW8nAoWuz0yTF0iP5APFLPsP3s1lka2JZc1F7y', {
        api_host: 'https://app.posthog.com',
      });
    } else if (window.location.pathname.startsWith('/video/product')) {
      posthog.init('phc_hfcuPWQgn0mzmmqARDMhueaN3B766ATjtobd7QOaAnL', {
        api_host: 'https://app.posthog.com',
      });
    }
  }
};

initializePostHog();

const decodeUrlParams = (encodedParams) => {
  try {
    const decodedString = atob(encodedParams.replace(/-/g, '+').replace(/_/g, '/'));
    const params = JSON.parse(decodedString);
    console.log('Decoded URL parameters:', params);
    return params;
  } catch (error) {
    console.error('Error decoding URL parameters:', error);
    return null;
  }
};

const extractDecodedParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const encodedParams = urlParams.get('params');

  if (encodedParams) {
    return decodeUrlParams(encodedParams);
  }
  return null;
};

const VyuVideo: React.FC = () => {
  const currentUrl = window.location.href
  const urlObj = new URL(currentUrl)
  const uId = urlObj?.searchParams?.get('uId');

  // Extract decoded params once and store in a variable (not state)
  const decodedUrlParams = extractDecodedParams();

  const location = useLocation()
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  const [showHowToScan, setShowHowToScan] = useState(false)
  const [cameraPermission, setCameraPermission] = useState<boolean | null>(null)
  const [locationPermission, setLocationPermission] = useState<boolean | null>(null)
  const [pageLoading, setLoading] = useState(true)
  const [videoSDK, setVideoSDK] = useState<VideoSDK | null>(null)
  const [instructionVisible, setInstructionVisible] = useState(false)
  const [brandEngagementVisible, setBrandEngagementVisible] = useState(false)
  const [isFirstLoad, setIsFirstLoad] = useState(false)
  const [ipAddress, setIpAddress] = useState<string | null>(null)
  const [ipLocation, setIpLocation] = useState<object | null>(null)
  const [brandEngagementData, setBrandEngagementData] = useState<object | null>(null)
  const [languageConfig, setLanguageConfig] = useState<any>()
  const [bandwidthResults, setBandwidthResults] = useState<any>()
  const [isBandwidthCalled, setIsBandwidthCalled] = useState(false)
  const [dummyViewfinderLoaded, setDummyViewfinderLoaded] = useState(false)
  const [showGenuinePopup, setShowGenuinePopup] = useState(false);
  const [showResultButtons, setShowResultButtons]= useState(false);
  const [genuinePopupConfig, setGenuinePopupConfig] = useState<any>(null);
  const [showReportPopup, setShowReportPopup] = useState(false);
  const [reportPopupConfig, setReportPopupConfig] = useState<any>(null)
  // Add state to track if loader image is cached
  const [loaderImageCached, setLoaderImageCached] = useState(false);
  // Add state to track if loader image is actually loaded and ready to display
  const [loaderImageReady, setLoaderImageReady] = useState(false);

  // Add state to track if screens have been dismissed
  const [splashScreenDismissed, setSplashScreenDismissed] = useState(false);
  const [howToScanDismissed, setHowToScanDismissed] = useState(false);
  
  // Add state to track geolocation fetch status and timing
  const [geolocationFetchStarted, setGeolocationFetchStarted] = useState(false);
  const [geolocationFetched, setGeolocationFetched] = useState(false);
  const geolocationStartTime = useRef<number>(0);

  const instructionShownRef = useRef(false)
  const bandwidthTestInitiated = useRef(false)
  const configLoadInitiated = useRef(false)
  const uidConfigLoadInitiated = useRef(false) // New ref for UID config
  const [appConfig, setAppConfig] = useState<any>(null)
  const [uidConfig, setUidConfig] = useState<any>(null) // New state for UID config
  const posthogEvent = usePosthogEvent()
  const permissionsGranted = false;

  // Preload the loader image when component mounts
  useEffect(() => {
    const cacheLoaderImage = async () => {
      try {
        await preloadImage(LOADER_IMAGE_URL);
        setLoaderImageCached(true);
        // Add a small delay to ensure the image is actually ready to display
        setTimeout(() => {
          setLoaderImageReady(true);
        }, 100);
        console.log('Loader image cached successfully');
      } catch (error) {
        webLogger.error('Error caching loader image', error);
        console.warn('Failed to cache loader image:', error);
        setLoaderImageCached(true); // Still proceed even if caching fails
        setLoaderImageReady(true); // Show fallback or proceed anyway
      }
    };

    cacheLoaderImage();
  }, []);

  // Add function to clear geolocation cache
  const clearGeolocationCache = () => {
    try {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_TIMESTAMP);
      console.log('Geolocation cache cleared');
    } catch (error) {
      webLogger.error('Error clearing geolocation cache', error);
      console.warn('Error clearing geolocation cache:', error);
    }
  };

  // Updated getCachedGeolocation function - only clear cache when actually needed
  const getCachedGeolocation = async (): Promise<{ lat: number, long: number }> => {
    try {
      // First, try to use cached location if available and not expired
      const cached = localStorage.getItem(CACHE_KEY);
      const timestamp = localStorage.getItem(CACHE_TIMESTAMP);

      if (cached && timestamp) {
        const age = Date.now() - parseInt(timestamp);
        if (age < CACHE_DURATION) {
          const cachedCoords = JSON.parse(cached);
          console.log('Using cached coordinates:', cachedCoords);
          return cachedCoords;
        }
      }

      // Only check permission status if we don't have valid cache
      // This prevents unnecessary cache clearing on iOS
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
        console.log('Permission status:', permissionStatus.state);

        // Only clear cache if explicitly denied
        if (permissionStatus.state === 'denied') {
          console.log('Permission denied, clearing cache');
          clearGeolocationCache();
          throw new Error('Geolocation permission denied');
        }
      } catch (permError) {
        webLogger.error('Error checking geolocation permission', permError);
        console.warn('Error checking geolocation permission:', permError);
        // On iOS, permission query might fail, but don't clear cache for this
      }

    } catch (error) {
      webLogger.error('Error checking geolocation cache', error);
      console.warn('Error checking geolocation cache:', error);
      // Only throw if it's a permission denied error
      if (error.message && error.message.includes('permission denied')) {
        throw error;
      }
    }

    // Fetch fresh coordinates
    return new Promise((resolve, reject) => {
      console.log('Fetching fresh coordinates...');

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            long: position.coords.longitude
          };

          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify(coords));
            localStorage.setItem(CACHE_TIMESTAMP, Date.now().toString());
            webLogger.info('Coordinates fetched and cached', coords);
            console.log('Fresh coordinates fetched and cached:', coords);
          } catch (error) {
            webLogger.error('Error saving coordinates to cache', error);
            console.warn('Error saving coordinates to cache:', error);
          }

          resolve(coords);
        },
        (error) => {
          console.error('Geolocation error:', error.message, 'Code:', error.code);

          if (error.code === error.PERMISSION_DENIED) {
            console.log('User denied geolocation permission');
            clearGeolocationCache();
            reject(error);
            return;
          }

          // For other errors, try to use expired cache as fallback
          try {
            const cachedData = localStorage.getItem(CACHE_KEY);
            if (cachedData) {
              const cachedCoords = JSON.parse(cachedData);
              console.log('Using expired cache as fallback:', cachedCoords);
              resolve(cachedCoords);
              return;
            }
          } catch (cacheError) {
            webLogger.error('Error retrieving expired cache', cacheError);
            console.warn('Error retrieving expired cache:', cacheError);
          }

          reject(error);
        },
        {
          enableHighAccuracy: false,
          timeout: 15000, // Increased timeout to 15 seconds
          maximumAge: 5 * 60 * 1000,
        }
      );
    });
  };

  // New function to handle background geolocation fetch with timing
  const handleBackgroundGeolocation = async () => {
    if (geolocationFetchStarted) {
      console.log('Geolocation fetch already started, skipping...');
      return;
    }

    if (!navigator.geolocation) {
      console.warn('Geolocation not supported by this browser.');
      posthogEvent.sendPostHogEvent('geolocation_background_fetch_failed', {
        error: 'geolocation_not_supported',
        source: 'browser_unsupported',
        clientName: decodedUrlParams?.clientName || null,
        uId: decodedUrlParams?.uId || uId,
        clientId: decodedUrlParams?.clientId || uId,
      });
      return;
    }

    setGeolocationFetchStarted(true);
    geolocationStartTime.current = Date.now();

    try {
      console.log('Starting background geolocation fetch...');
      const coords = await getCachedGeolocation();
      
      const fetchDuration = Date.now() - geolocationStartTime.current;
      
      // Update location with fetched coordinates
      setIpLocation(prevLocation => ({
        ...prevLocation,
        latitude: coords.lat,
        longitude: coords.long,
        source: 'geolocation' // Add source to track which location is being used
      }));
      
      setGeolocationFetched(true);
      
      console.log('Background geolocation fetch successful:', coords, `Duration: ${fetchDuration}ms`);
      
      posthogEvent.sendPostHogEvent('geolocation_background_fetch_success', {
        clientName: decodedUrlParams?.clientName || null,
        uId: decodedUrlParams?.uId || uId,
        clientId: decodedUrlParams?.clientId || uId,
        latitude: coords.lat,
        longitude: coords.long,
        fetchDurationMs: fetchDuration,
        source: 'background_fetch'
      });

      // Update VideoSDK if it's already initialized
      if (videoSDK) {
        const updatedLocation = {
          ...ipLocation,
          latitude: coords.lat,
          longitude: coords.long,
          source: 'geolocation'
        };
        videoSDK.updateLocation?.(updatedLocation);
        console.log('Updated VideoSDK with geolocation coordinates');
      }

    } catch (error) {
      const fetchDuration = Date.now() - geolocationStartTime.current;
      
      webLogger.error('Background geolocation fetch failed', error);
      console.error('Background geolocation fetch failed:', error.message, `Duration: ${fetchDuration}ms`);

      posthogEvent.sendPostHogEvent('geolocation_background_fetch_failed', {
        error: error.message,
        errorCode: error.code || 'unknown',
        source: 'background_fetch',
        clientName: decodedUrlParams?.clientName || null,
        uId: decodedUrlParams?.uId || uId,
        clientId: decodedUrlParams?.clientId || uId,
        fetchDurationMs: fetchDuration
      });

      console.log('Continuing with IP-based location due to geolocation error');
    }
  };

  

  const getLanguageFromURL = () => {
    if (decodedUrlParams && decodedUrlParams?.lang) {
      const lang = decodedUrlParams.lang.toLowerCase();
      return LANGUAGE_CONFIG[lang] ? lang : 'en';
    }

    const urlParams = new URLSearchParams(window.location.search)
    let lang = urlParams.get('lang')
    lang = lang ? lang.toLowerCase() : 'en'
    return LANGUAGE_CONFIG[lang] ? lang : 'en'
  }

  const loadAppConfig = async () => {
    if (configLoadInitiated.current || appConfig) {
      console.log('Config already loading/loaded, skipping...', { 
        configLoadInitiated: configLoadInitiated.current, 
        appConfigExists: !!appConfig 
      });
      return appConfig;
    }

    configLoadInitiated.current = true;
    console.log('Starting app config load...');

    try {
      let environment = 'prod';
      if (config.API_URL.startsWith('https://d.') || config.API_URL.startsWith('http://d.')) {
        environment = 'dev';
      } else if (config.API_URL.startsWith('https://q.') || config.API_URL.startsWith('http://q.')) {
        environment = 'qa';
      } else if (config.API_URL.startsWith('https://s.') || config.API_URL.startsWith('http://s.')) {
        environment = 'stage';
      }

      console.log('Loading app config from environment:', environment);
      const response = await fetch(`${config.API_URL}/api/config/v1/VyuLiveStream/${environment}/appConfig`);

      // Check if the response is ok (status 200-299)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }

      const configData = await response.json();
      console.log('App config loaded successfully:', configData);
      if (configData && configData.data) {
        setAppConfig(configData.data);
        posthogEvent.sendPostHogEvent('app_config_fetched', {
          clientName: decodedUrlParams?.clientName || null,
          source: decodedUrlParams?.source || null,
          platform: decodedUrlParams?.platform || null,
          uId: decodedUrlParams?.uId || uId,
          clientId: decodedUrlParams?.clientId || uId,
        });
      }
      return configData;
    } catch (error) {
      webLogger.error('Error loading app config', error);
      posthogEvent.sendPostHogEvent('app_config_failed', {
        clientName: decodedUrlParams?.clientName || null,
        source: decodedUrlParams?.source || null,
        platform: decodedUrlParams?.platform || null,
        uId: decodedUrlParams?.uId || uId,
        clientId: decodedUrlParams?.clientId || uId,
        error: error.message,
      });
      console.error('Error loading app config:', error);
      setAppConfig({});
      return null;
    } finally {
      // Don't reset configLoadInitiated here to prevent duplicate calls
    }
  };

  const loadUidConfig = async (appConfig = {}) => {
    if (uidConfigLoadInitiated.current || uidConfig !== null) {
      console.log('UID config already loading/loaded, skipping...', { 
        uidConfigLoadInitiated: uidConfigLoadInitiated.current, 
        uidConfigExists: uidConfig !== null 
      });
      return uidConfig;
    }

    // Get the original uId from URL params
    const originalUId = decodedUrlParams?.uId || uId;
    if (!originalUId) {
      console.log('No uId found, setting default empty UID config');
      setUidConfig({});
      return {};
    }

    // Get the mapped client ID from appConfig
    const clientIdMappings = appConfig?.data?.clientIdMappings || {};
    const mappedClientId = clientIdMappings[originalUId];

    if (!mappedClientId) {
      console.log(`No mapping found for uId: ${originalUId}, setting default empty UID config`);
      setUidConfig({});
      return {};
    }

    // Use the mapped client ID as currentUId
    let currentUId = mappedClientId;

    console.log(`Mapped uId ${originalUId} to client: ${currentUId}`);

    uidConfigLoadInitiated.current = true;
    console.log('Starting UID config load for:', currentUId);

    try {
      let environment = 'prod';
      if (config.API_URL.startsWith('https://d.') || config.API_URL.startsWith('http://d.')) {
        environment = 'dev';
      } else if (config.API_URL.startsWith('https://q.') || config.API_URL.startsWith('http://q.')) {
        environment = 'qa';
      } else if (config.API_URL.startsWith('https://s.') || config.API_URL.startsWith('http://s.')) {
        environment = 'stage';
      }

      console.log('Loading UID config from environment:', environment, 'for mapped client:', currentUId, 'original uId:', originalUId);
      currentUId = currentUId.toLowerCase();
      const response = await fetch(`${config.API_URL}/api/config/v1/VyuLiveStream/${environment}/${currentUId}`);

      // Check if the response is ok (status 200-299)
      if (!response.ok) {
        console.warn(`UID config API returned ${response.status} for client: ${currentUId}. Using default empty config.`);
        setUidConfig({});
        return {};
      }

      const configData = await response.json();
      console.log('UID config loaded successfully:', configData);
      if (configData && configData.data) {
        setUidConfig(configData.data);
        posthogEvent.sendPostHogEvent('uid_config_fetched', {
          clientName: decodedUrlParams?.clientName || null,
          source: decodedUrlParams?.source || null,
          platform: decodedUrlParams?.platform || null,
          uId: originalUId,
          mappedClientId: currentUId,
          clientId: decodedUrlParams?.clientId || uId,
        });
      } else {
        setUidConfig({});
      }
      return configData;
    } catch (error) {
      webLogger.error('Error loading UID config', error);
      posthogEvent.sendPostHogEvent('uid_config_failed', {
        clientName: decodedUrlParams?.clientName || null,
        source: decodedUrlParams?.source || null,
        platform: decodedUrlParams?.platform || null,
        uId: originalUId,
        mappedClientId: currentUId,
        clientId: decodedUrlParams?.clientId || uId,
        error: error.message,
      });
      console.error('Error loading UID config:', error);
      console.log('Setting default empty UID config due to error');
      setUidConfig({});
      return null;
    } finally {
      // Don't reset uidConfigLoadInitiated here to prevent duplicate calls
    }
  };

  const runBandwidthTest = async () => {
    try {
      if (bandwidthTestInitiated.current || isBandwidthCalled) {
        return;
      }
      bandwidthTestInitiated.current = true;
      setIsBandwidthCalled(true);

      console.log('Starting bandwidth test...');

      const results = await performBandwidthTest();

      setBandwidthResults(results);

      posthogEvent.sendPostHogEvent('bandwidth_test_completed', {
        avgSpeedMbps: results.avgSpeedMbps,
        bytesReceived: results.bytesReceived,
        durationMs: results.durationMs,
        speedMbps: results.speedMbps,
        testsCompleted: results.testsCompleted,
        largestPacketKB: results.largestPacketKB,
        error: results.error || null,
        clientName: decodedUrlParams?.clientName || null,
        source: decodedUrlParams?.source || null,
        platform: decodedUrlParams?.platform || null,
        uId: decodedUrlParams?.uId || uId,
        clientId: decodedUrlParams?.clientId || uId,
      });

      console.log('Bandwidth test completed:', results);
      return results;

    } catch (error) {
      webLogger.error('Bandwidth test failed', error);
      console.error('Bandwidth test failed:', error);
      posthogEvent.sendPostHogEvent('bandwidth_test_failed', {
        error: error.message,
        clientName: decodedUrlParams?.clientName || null,
        source: decodedUrlParams?.source || null,
        platform: decodedUrlParams?.platform || null,
        uId: decodedUrlParams?.uId || uId,
        clientId: decodedUrlParams?.clientId || uId,
      });

      const defaultResults = {
        bytesReceived: 0,
        durationMs: 0,
        speedMbps: 1,
        avgSpeedMbps: 1,
        testsCompleted: 0,
        error: error.message,
      };
      setBandwidthResults(defaultResults);
      return defaultResults;
    }
  };

  const fetchIpAndLocation = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/')
      const data = await response.json()
      setIpAddress(data);
      setIpLocation({
        city: data.city,
        region: data.region,
        country: data.country_name,
        latitude: data.latitude,
        longitude: data.longitude,
        source: 'ip' // Add source to track which location is being used
      })
      posthogEvent.sendPostHogEvent('ip_and_location_captured', {
        ip: data.ip,
        city: data.city,
        region: data.region,
        country: data.country_name,
        latitude: data.latitude,
        longitude: data.longitude,
        clientName: decodedUrlParams?.clientName || null,
        clientId: decodedUrlParams?.clientId || uId,
        uId: decodedUrlParams?.uId || uId,
      })
    } catch (error) {
      setIpLocation({
        city: '',
        region: '',
        country: '',
        latitude: '',
        longitude: '',
        source: 'fallback'
      })
      webLogger.error('Error fetching IP and location', error)
      console.error('Error fetching IP and location:', error)
      posthogEvent.sendPostHogEvent('ip_and_location_failed', {
        error: error.message,
        clientName: decodedUrlParams?.clientName || null,
        clientId: decodedUrlParams?.clientId || uId,
        uId: decodedUrlParams?.uId || uId,
      })
    }
  }

  const requestCameraPermission = async () => {
    try {
      const isDesktop = !/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
      await navigator.mediaDevices.getUserMedia({
        video: { facingMode: isDesktop ? { ideal: 'user' } : { ideal: 'environment' } },
      })
      posthogEvent.sendPostHogEvent(posthogEvents.cameraPermissionGranted, {
        clientName: decodedUrlParams?.clientName || null,
      })
      setCameraPermission(true)
      setIsFirstLoad(true)

      instructionShownRef.current = false;

      setDummyViewfinderLoaded(false);

    } catch (err) {
      webLogger.error('Camera permission denied', err)
      posthogEvent.sendPostHogEvent(posthogEvents.cameraPermissionGranted, {
        error: err,
        clientName: decodedUrlParams?.clientName || null,
        clientId: decodedUrlParams?.clientId || uId,
        uId: decodedUrlParams?.uId || uId,
      })
      console.error('Camera permission denied:', err)
      setCameraPermission(false)
      alert('Camera permission is denied. Please enable camera access in your browser settings.')
    }
  }

  // Updated requestLocationPermission to immediately proceed with IP location
  const requestLocationPermission = async () => {
    console.log('Location permission requested - proceeding with IP-based location immediately');
    
    // Set location permission to true immediately to proceed with IP-based location
    setLocationPermission(true);
    
    posthogEvent.sendPostHogEvent('location_permission_granted_ip_based', {
      source: 'ip_location',
      clientName: decodedUrlParams?.clientName || null,
      uId: decodedUrlParams?.uId || uId,
      clientId: decodedUrlParams?.clientId || uId,
    });

    // Start background geolocation fetch (non-blocking)
    setTimeout(() => {
      handleBackgroundGeolocation();
    }, 100); // Small delay to ensure UI proceeds without blocking
  };

  const handleSplashScreenStart = () => {
    console.log('Starting scanning from splash screen');
    setShowSplashScreen(false);
    setSplashScreenDismissed(true);

    // Check if howToScan feature flag is enabled
    const showHowToScanFlag = uidConfig?.featureFlags?.showHowToScan;
    if (showHowToScanFlag) {
      setShowHowToScan(true);
    }

    // Track splash screen interaction
    posthogEvent.sendPostHogEvent('splash_screen_start_clicked', {
      clientName: decodedUrlParams?.clientName || null,
      source: decodedUrlParams?.source || null,
      platform: decodedUrlParams?.platform || null,
      uId: decodedUrlParams?.uId || uId,
      clientId: decodedUrlParams?.clientId || uId,
    });
  };

  const handleSplashScreenLanguage = () => {
    console.log('Choose language clicked from splash screen');

    // Track language selection
    posthogEvent.sendPostHogEvent('splash_screen_language_clicked', {
      clientName: decodedUrlParams?.clientName || null,
      source: decodedUrlParams?.source || null,
      platform: decodedUrlParams?.platform || null,
      uId: decodedUrlParams?.uId || uId,
      clientId: decodedUrlParams?.clientId || uId,
    });

    // For now, just proceed - you can implement language selection modal here
    setShowSplashScreen(false);
    setSplashScreenDismissed(true);

    // Check if howToScan feature flag is enabled
    const showHowToScanFlag = uidConfig?.featureFlags?.showHowToScan;
    if (showHowToScanFlag) {
      setShowHowToScan(true);
    }
  };

  useEffect(() => {
    if (!showGenuinePopup && videoSDK && showResultButtons) {
      videoSDK.showResultButtons();
    }
  }, [showGenuinePopup, videoSDK, showResultButtons]);

  const showGenuineResultPopup = (config: any) => {
  setGenuinePopupConfig(config);
  setShowGenuinePopup(true);
  };

  const handleCloseGenuinePopup = () => {
  setShowGenuinePopup(false);
  setGenuinePopupConfig(null);
  setShowResultButtons(true)
  };

  const showReportNowPopup = (data: any) => {
    setReportPopupConfig(data);
    setShowReportPopup(true);
  };

  const handleCloseReportPopup = () => {
    setShowReportPopup(false);
    setReportPopupConfig(null);
  };
  
  const handleHowToScanStart = () => {
    console.log('Starting scanning from how to scan screen');
    setShowHowToScan(false);
    setHowToScanDismissed(true);

    // Track how to scan interaction
    posthogEvent.sendPostHogEvent('how_to_scan_start_clicked', {
      clientName: decodedUrlParams?.clientName || null,
      source: decodedUrlParams?.source || null,
      platform: decodedUrlParams?.platform || null,
      uId: decodedUrlParams?.uId || uId,
      clientId: decodedUrlParams?.clientId || uId,
    });
  };

    const toggleBrandEngagement = (brandEngagementData?: any) => {
      setBrandEngagementVisible(!brandEngagementVisible);
      if (brandEngagementData) {
        setBrandEngagementData(brandEngagementData);
      }
    }

  const handleInstructionVisibility = (visible: boolean) => {
    console.log('Changing instruction visibility to:', visible)

    if (instructionVisible && !visible && videoSDK) {
      console.log('Instruction hidden, calling sendVideo')
      videoSDK.sendVideo()

      const dummyVideoElement = document.getElementById('dummy-vyu-stream') as HTMLVideoElement | null
      if (dummyVideoElement) {
        console.log('Removing dummy viewfinder after instruction dismissed')
        const stream = dummyVideoElement.srcObject as MediaStream
        if (stream) {
          stream.getTracks().forEach((track) => track.stop())
        }
        dummyVideoElement.remove()
      }
    }

    setInstructionVisible(false)
  }

  useEffect(() => {
    if (bandwidthResults) {
      if (videoSDK) {
        videoSDK.setBandwidth(bandwidthResults);
      }
    }
  }, [bandwidthResults])

useEffect(() => {
  const initializeApp = async () => {
    // Only reset flags if this is a fresh page load (not React re-render)
    if (!configLoadInitiated.current && !uidConfigLoadInitiated.current) {
      console.log('Fresh page load - resetting all states and refs');
      instructionShownRef.current = false;
      bandwidthTestInitiated.current = false;
      setSplashScreenDismissed(false);
      setHowToScanDismissed(false);
      setShowSplashScreen(true);
      setShowHowToScan(false);
      setGeolocationFetchStarted(false);
      setGeolocationFetched(false);
    } else {
      console.log('React re-render detected - skipping state resets');
      return; // Exit early to prevent duplicate API calls
    }

    let loadedAppConfig = appConfig;
    let loadedUidConfig = uidConfig;
    
    // Load app config only if not already loaded
    if (!loadedAppConfig) {
      console.log('Loading app config...');
      try {
        loadedAppConfig = await loadAppConfig();
      } catch (error) {
        console.error('App config loading failed:', error);
        loadedAppConfig = {};
      }
    } else {
      console.log('App config already loaded, skipping API call');
    }

    // Load UID config only if not already loaded
    if (loadedUidConfig === null) {
      console.log('Loading UID config...');
      try {
        loadedUidConfig = await loadUidConfig(loadedAppConfig);
      } catch (error) {
        console.error('UID config loading failed:', error);
        loadedUidConfig = {};
      }
    } else {
      console.log('UID config already loaded, skipping API call');
    }

    let appSessionId = sessionStorage.getItem('appSessionId')
    const lang = getLanguageFromURL();
    console.log('Using configs for language setup:', { appConfig: loadedAppConfig, uidConfig: loadedUidConfig });
    
    // Merge language configurations with uidConfig taking precedence
    const appLanguageConfig = (loadedAppConfig as any)?.data?.languageConfig || {};
    const uidLanguageConfig = (loadedUidConfig as any)?.data?.languageConfig || {};
    const mergedLanguageConfig = { ...appLanguageConfig, ...uidLanguageConfig };
    const langConfig = mergedLanguageConfig[lang] || mergedLanguageConfig['en'];
    setLanguageConfig(langConfig)

    if (!appSessionId) {
      appSessionId = uuid()
      sessionStorage.setItem('appSessionId', appSessionId)
    }

    posthogEvent.sendPostHogEvent(posthogEvents.verificationExperienceStart, {
      clientName: decodedUrlParams?.clientName || null,
      source: decodedUrlParams?.source || null,
      version: decodedUrlParams?.version || null,
      deviceId: decodedUrlParams?.deviceId || null,
      platform: decodedUrlParams?.platform || null,
      model: decodedUrlParams?.model || null,
      osVersion: decodedUrlParams?.osVersion || null,
      env: decodedUrlParams?.env || null,
      clientId: decodedUrlParams?.clientId || uId,
      uId: decodedUrlParams?.uId || uId,
      appSessionId,
    })

    //runBandwidthTest();

// Complete checkPermissions function - always show location permission screen
const checkPermissions = async () => {
  try {
    localStorage.setItem('RetryVideoLoadError', '')
    if (permissionsGranted) {
      try {
        const isDesktop = !/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
        await navigator.mediaDevices.getUserMedia({
          video: { facingMode: isDesktop ? { ideal: 'user' } : { ideal: 'environment' } },
        })
        posthogEvent.sendPostHogEvent(posthogEvents.cameraPermissionGranted, {
          clientName: decodedUrlParams?.clientName || null,
          source: decodedUrlParams?.source || null,
          version: decodedUrlParams?.version || null,
          deviceId: decodedUrlParams?.deviceId || null,
          platform: decodedUrlParams?.platform || null,
          model: decodedUrlParams?.model || null,
          osVersion: decodedUrlParams?.osVersion || null,
          env: decodedUrlParams?.env || null,
          clientId: decodedUrlParams?.clientId || uId,
          uId: decodedUrlParams?.uId || uId,
          appSessionId,
        })

        posthogEvent.sendPostHogEvent(posthogEvents.locationPermissionGranted, {
          clientName: decodedUrlParams?.clientName || null,
          source: decodedUrlParams?.source || null,
          version: decodedUrlParams?.version || null,
          deviceId: decodedUrlParams?.deviceId || null,
          platform: decodedUrlParams?.platform || null,
          model: decodedUrlParams?.model || null,
          osVersion: decodedUrlParams?.osVersion || null,
          env: decodedUrlParams?.env || null,
          clientId: decodedUrlParams?.clientId || uId,
          uId: decodedUrlParams?.uId || uId,
          appSessionId,
        })
        setCameraPermission(true)
        setLocationPermission(true)
      } catch (error) {
        webLogger.error('Camera permission error', error)
        console.error('Camera permission error:', error)
        const cameraStatus = await navigator.permissions.query({ name: 'camera' as PermissionName })
        const cameraGranted = cameraStatus.state === 'granted'
        setCameraPermission(cameraGranted)
        setLocationPermission(true)
      }
    } else {
      // Check camera permission
      const cameraStatus = await navigator.permissions.query({ name: 'camera' as PermissionName })
      const cameraGranted = cameraStatus.state === 'granted'
      setCameraPermission(cameraGranted)

      // Always show location permission screen - don't auto-grant
      console.log('Checking location permission status...');
      let locationGranted = false;

      try {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

        if (isIOS) {
          // On iOS, check for very recent cache to detect "Always Allow"
          try {
            const cached = localStorage.getItem(CACHE_KEY);
            const timestamp = localStorage.getItem(CACHE_TIMESTAMP);
            
            if (cached && timestamp) {
              const age = Date.now() - parseInt(timestamp);
              // Very short window - only if location was accessed very recently (2 minutes)
              if (age < 2 * 60 * 1000) {
                console.log('iOS: Very recent location access detected - Always Allow');
                locationGranted = true;
              } else {
                console.log('iOS: No recent location access - showing permission screen');
                locationGranted = false;
              }
            } else {
              console.log('iOS: No location data - showing permission screen');
              locationGranted = false;
            }
          } catch (error) {
            webLogger.error('Error checking location status', error);
            console.log('iOS: Error checking location status - showing permission screen');
            locationGranted = false;
          }
        } else {
          // Non-iOS: use standard permission API
          const locationStatus = await navigator.permissions.query({ name: 'geolocation' });
          locationGranted = locationStatus.state === 'granted';
          console.log('Location permission status:', locationStatus.state);
        }

      } catch (err) {
        webLogger.error('Error checking location permission', err);
        console.warn('Error checking location permission - showing permission screen:', err);
        locationGranted = false;
      }

      setLocationPermission(locationGranted);

      // If location is already granted, start background geolocation immediately
      if (locationGranted) {
        console.log('Location already granted, starting background fetch');
        setTimeout(() => {
          handleBackgroundGeolocation();
        }, 1000);
      }
    }
  } catch (err) {
    webLogger.error('Error checking permissions', err)
    console.error('Error checking permissions:', err)
  } finally {
    setLoading(false)
  }
}

    checkPermissions()
  };
  console.log('Initializing VyuVideo component');
  initializeApp();
}, [])

  useEffect(() => {
    fetchIpAndLocation()
  }, []);

  useEffect(() => {
    instructionShownRef.current = false;

    if (locationPermission && cameraPermission && isFirstLoad) {
      let dummyVideoElement = document.getElementById('dummy-vyu-stream') as HTMLVideoElement | null

      if (!dummyVideoElement) {
        console.log('Creating dummy viewfinder')
        dummyVideoElement = document.createElement('video')
        dummyVideoElement.id = 'dummy-vyu-stream'
        dummyVideoElement.autoplay = true
        dummyVideoElement.playsInline = true
        dummyVideoElement.style.setProperty('width', '100vw', 'important')
        dummyVideoElement.style.setProperty('height', '100vh', 'important')
        dummyVideoElement.style.setProperty('object-fit', 'cover', 'important')
        dummyVideoElement.style.setProperty('position', 'absolute', 'important')
        dummyVideoElement.style.setProperty('top', '0', 'important')
        dummyVideoElement.style.setProperty('left', '0', 'important')

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

        const markAsLoaded = () => {
          if (typeof markAsLoadedTimeoutId === 'number') {
            clearTimeout(markAsLoadedTimeoutId);
          }

          if (!dummyViewfinderLoaded && !instructionShownRef.current) {
            console.log('Dummy viewfinder marked as loaded')

            instructionShownRef.current = true;

            setTimeout(() => {
              setDummyViewfinderLoaded(true)
              setInstructionVisible(true)

              posthogEvent.sendPostHogEvent('dummy_viewfinder_loaded', {
                result: 'success',
                clientName: decodedUrlParams?.clientName || null,
              })
            }, isIOS ? 500 : 50);
          }
        };

        dummyVideoElement.addEventListener('canplay', markAsLoaded)

        const markAsLoadedTimeoutId = setTimeout(() => {
          if (!dummyViewfinderLoaded && !instructionShownRef.current) {
            console.log('Dummy viewfinder loaded via timeout fallback')
            markAsLoaded()
          }
        }, isIOS ? 2000 : 1500) as unknown as number

        document.body.appendChild(dummyVideoElement)

        const isDesktop = !/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
        navigator.mediaDevices
          .getUserMedia({ video: { facingMode: isDesktop ? { ideal: 'user' } : { ideal: 'environment' } } })
          .then((stream) => {
            if (dummyVideoElement) {
              dummyVideoElement.srcObject = stream
              console.log('Stream assigned to dummy viewfinder')
            }
          })
          .catch((err) => {
            webLogger.error('Failed to start dummy video stream', err)
            console.error('Failed to start dummy video stream:', err)
            posthogEvent.sendPostHogEvent('dummy_viewfinder_failed', {
              result: 'failed',
              error: err.message,
              clientName: decodedUrlParams?.clientName || null,
            })

            setTimeout(() => {
              if (!dummyViewfinderLoaded && !instructionShownRef.current) {
                console.log('Forcing instruction visible despite camera error')
                markAsLoaded()
              }
            }, isIOS ? 2000 : 1500)
          })
      }
    }

    return () => {
      if (!locationPermission || !cameraPermission || !isFirstLoad) {
        const dummyVideoElement = document.getElementById('dummy-vyu-stream') as HTMLVideoElement | null
        if (dummyVideoElement) {
          console.log('Cleaning up dummy viewfinder')
          const stream = dummyVideoElement.srcObject as MediaStream
          if (stream) {
            stream.getTracks().forEach((track) => track.stop())
          }
          dummyVideoElement.remove()
          setDummyViewfinderLoaded(false)

          instructionShownRef.current = false;
        }
      }
    }
  }, [locationPermission, cameraPermission, isFirstLoad, dummyViewfinderLoaded])

  useEffect(() => {
    const currentUrl = window.location.href
    const urlObj = new URL(currentUrl)
    const flow = FLOW.VIDEO_STREAM;

    // Check if all prerequisite screens are dismissed
    const allScreensDismissed = () => {
      const showSplashScreenFlag = uidConfig?.featureFlags?.showSplashScreen;
      const showHowToScanFlag = uidConfig?.featureFlags?.showHowToScan;

      console.log('Checking screens dismissed:', {
        showSplashScreenFlag,
        showHowToScanFlag,
        splashScreenDismissed,
        howToScanDismissed
      });

      // If splash screen is enabled but not dismissed, return false
      if (showSplashScreenFlag && !splashScreenDismissed) {
        console.log('Splash screen enabled but not dismissed, blocking SDK init');
        return false;
      }

      // If how-to-scan is enabled but not dismissed, return false
      if (showHowToScanFlag && !howToScanDismissed) {
        console.log('How-to-scan enabled but not dismissed, blocking SDK init');
        return false;
      }

      console.log('All screens dismissed or feature flags disabled, allowing SDK init');
      return true;
    };

    // Updated condition to handle empty uidConfig properly
    if (cameraPermission && locationPermission && ipLocation && appConfig && uidConfig !== null && !videoSDK && allScreensDismissed()) {
      setLoading(false);
      console.log('Initializing main VideoSDK - all screens dismissed and permissions granted')
      let videoElement = document.getElementById('vyu-stream') as HTMLVideoElement | null

      if (!videoElement) {
        videoElement = document.createElement('video')
        videoElement.id = 'vyu-stream'
        videoElement.autoplay = true
        videoElement.playsInline = true
        videoElement.style.setProperty('width', '100vw', 'important')
        videoElement.style.setProperty('height', '100vh', 'important')
        videoElement.style.setProperty('object-fit', 'cover', 'important')
        videoElement.style.setProperty('position', 'absolute', 'important')
        videoElement.style.setProperty('top', '0', 'important')
        videoElement.style.setProperty('left', '0', 'important')

        const vyuWrapper = document.getElementById('vyu-wrapper')
        if (vyuWrapper) {
          vyuWrapper.appendChild(videoElement)
        }
      }

      const sdkInstance = new VideoSDK(videoElement)
      const sendVideo = !isFirstLoad
      const clientId = decodedUrlParams?.uId || decodedUrlParams?.clientId || uId;
      sdkInstance.init(
        flow,
        sendVideo,
        toggleBrandEngagement,
        posthogEvent,
        ipLocation,
        languageConfig,
        clientId,
        appConfig,
        uidConfig,
        ipAddress,
        showGenuineResultPopup,
        showReportNowPopup,
      )

      setVideoSDK(sdkInstance);
      posthogEvent.sendPostHogEvent('sdk_init', {
        flow,
        sendVideo,
        ipLocation,
        geolocationStatus: geolocationFetched ? 'fetched' : geolocationFetchStarted ? 'fetching' : 'not_started',
        locationSource: ipLocation?.source || 'unknown',
        ...decodedUrlParams,
        clientId: decodedUrlParams?.clientId || uId,
        uId: decodedUrlParams?.uId || uId,
      })
      console.log('VideoSDK initialized with sendVideo:', sendVideo)

      videoElement.addEventListener('error', (error) => {
        console.error('Video stream error:', error)
      })
    }

    const handleVisibilityChange = () => {
      const clientId = decodedUrlParams?.uId || decodedUrlParams?.clientId || uId;
      if (document.hidden) {
        videoSDK?.stop()
      } else if (!brandEngagementVisible && !instructionVisible) {
        videoSDK?.init(flow, true, toggleBrandEngagement, posthogEvent, ipLocation, languageConfig, clientId, appConfig, uidConfig, showGenuineResultPopup, showReportNowPopup)
        posthogEvent.sendPostHogEvent('sdk_init_visibility_changed', {
          flow,
          sendVideo: true,
          ipLocation,
          geolocationStatus: geolocationFetched ? 'fetched' : geolocationFetchStarted ? 'fetching' : 'not_started',
          locationSource: ipLocation?.source || 'unknown',
          ...decodedUrlParams,
          clientId: decodedUrlParams?.clientId || uId,
          uId: decodedUrlParams?.uId || uId,
        })
      } else {
        //videoSDK?.init(flow, false, toggleBrandEngagement, posthogEvent, ipLocation, languageConfig, clientId, appConfig, uidConfig)
        posthogEvent.sendPostHogEvent('sdk_init_visibility_changed_with_popup_visible', {
          flow,
          sendVideo: false,
          ipLocation,
          geolocationStatus: geolocationFetched ? 'fetched' : geolocationFetchStarted ? 'fetching' : 'not_started',
          locationSource: ipLocation?.source || 'unknown',
          ...decodedUrlParams,
          clientId: decodedUrlParams?.clientId || uId,
          uId: decodedUrlParams?.uId || uId,
        })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [cameraPermission, locationPermission, videoSDK, brandEngagementVisible, bandwidthResults, isFirstLoad, instructionVisible, appConfig, ipLocation, uidConfig, splashScreenDismissed, howToScanDismissed, geolocationFetched, geolocationFetchStarted])

  // Updated logic to handle empty uidConfig properly
  const shouldShowSplashScreen = appConfig && uidConfig !== null && !splashScreenDismissed && uidConfig?.featureFlags?.showSplashScreen;

  const shouldShowHowToScan = appConfig && uidConfig !== null && !howToScanDismissed && splashScreenDismissed && uidConfig?.featureFlags?.showHowToScan;
  const shouldShowGenuinePopup = appConfig && uidConfig !== null && genuinePopupConfig && showGenuinePopup
  const shouldShowReportPopup = appConfig && uidConfig !== null && reportPopupConfig && showReportPopup
  return (
    <div className='VyuVideoStreamWrapper' onContextMenu={(e) => e.preventDefault()}>
      {shouldShowSplashScreen && (
        <SplashScreen
          appConfig={appConfig}
          clientConfig={uidConfig}
          onStartScanning={handleSplashScreenStart}
          onChooseLanguage={handleSplashScreenLanguage}
          languageConfig={languageConfig}
        />
      )}

      {shouldShowHowToScan && (
        <HowToScan
          appConfig={appConfig}
          clientConfig={uidConfig}
          onStartScanning={handleHowToScanStart}
          languageConfig={languageConfig}
        />
      )}

      {/* Show loader while app is loading */}
      {pageLoading && (
        <div className='loader-wrapper'>
          {/* Show blue loader until image is ready */}
          {!loaderImageReady && (
            <div style={{
              width: '60px',
              height: '60px',
              border: '6px solid #e3f2fd',
              borderTop: '6px solid #2196f3',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }} />
          )}
          
          {/* Show the actual loader image when ready */}
          {loaderImageReady && (
            <img
              src={LOADER_IMAGE_URL}
              alt='Loading...'
              style={{
                opacity: loaderImageCached ? 1 : 0.8,
                transition: 'opacity 0.3s ease-in-out',
                maxWidth: 200,
                maxHeight: 200,
                height: 'auto'
              }}
              onLoad={() => {
                console.log('Loader image displayed successfully');
              }}
              onError={(e) => {
                console.error('Failed to load loader image');
                e.target.style.display = 'none';
              }}
            />
          )}
        </div>
      )}

      {/* Show main app content when not loading and no screens are shown */}
      {!pageLoading && !shouldShowSplashScreen && !shouldShowHowToScan && (
        <>
          {!cameraPermission && (
            <Permission
              name={languageConfig?.cameraAccessNeeded}
              text={languageConfig?.allowCameraAccess}
              image={PHONE}
              cta={languageConfig?.enableCamera}
              onClick={requestCameraPermission}
              posthogEvent={posthogEvent}
              decodedParams={decodedUrlParams}
              uId={decodedUrlParams?.uId || uId}
              clientConfig={uidConfig}
            />
          )}
          {cameraPermission && !locationPermission && (
            <Permission
              name={languageConfig?.locationAccessNeeded}
              text={languageConfig?.locationPurpose}
              image={CAMERA}
              cta={languageConfig?.enableLocation}
              onClick={requestLocationPermission}
              posthogEvent={posthogEvent}
              decodedParams={decodedUrlParams}
              uId={decodedUrlParams?.uId || uId}
              clientConfig={uidConfig}
            />
          )}
          {cameraPermission && locationPermission && (
            <div
              id='vyu-wrapper'
              className='VyuWrapper'
              style={{ width: '100%', height: '100%' }}
            />
          )}
          {cameraPermission && locationPermission && brandEngagementVisible && (
            <BrandEngagement setBrandEngagementVisible={setBrandEngagementVisible} brandEngagementData={brandEngagementData} />
          )}
          {cameraPermission && locationPermission && instructionVisible && dummyViewfinderLoaded && (
            <Instruction clientConfig={uidConfig} setInstructionVisible={handleInstructionVisibility} languageConfig={languageConfig} />
          )}
          
        </>
      )}
      {shouldShowGenuinePopup && (
        <GenuineVideoPopup
          isVisible={shouldShowGenuinePopup}
          onClose={handleCloseGenuinePopup}
          logoUrl={genuinePopupConfig?.logoUrl}
          videoUrl={genuinePopupConfig?.videoUrl}
          title={genuinePopupConfig?.title}
          closeButtonText={genuinePopupConfig?.closeButtonText}
          className="genuine-result-popup"
          styles={genuinePopupConfig?.styles}
      />
        )}
        {shouldShowReportPopup && (
          <ReportNow
            isVisible={shouldShowReportPopup}
            onClose={handleCloseReportPopup}
            config={reportPopupConfig}
            languageConfig={languageConfig}
            sessionId={reportPopupConfig?.sessionId}
            productInfo={reportPopupConfig?.productInfo}
            posthogEvent={posthogEvent}
          />
      )}
    </div>
  )
}

export default VyuVideo